/**
 * ChangeListPanel.jsx — every way this book's audio differs from its text.
 *
 * ⭐ WHY IT IS PART OF THE DELIVERABLE AND NOT A CONVENIENCE. Once a line can be split, a span removed or
 * (one day) a sentence restored, the audiobook and the book are two documents. Somebody will need to know
 * where they differ: an acquirer doing due diligence, a rights conversation, or the operator in two years
 * wondering why a sentence is missing. See LINE_EDIT_INSERT_EVALUATION.md §4.3.
 *
 * ⛔ THE THREE GROUPS ARE NOT EQUALLY SERIOUS, and flattening them would defeat the point.
 *
 *   1. The audio says something the book does not — an editorial rewrite, a removed span. The group a
 *      reviewer actually wants, and the only one that is ever a rights question.
 *   2. Structure only — a split. The words are provably identical: the concatenation invariant is checked
 *      on write and again on every read. Only the line boundaries and speakers moved.
 *   3. Attribution only — who says a line, and how. Not one word of the author's changes.
 *
 * ⚠️ Sits beside the Activity log, collapsed, for the same reason that is: it is a record to consult, not a
 * surface to work in. The count in the title is what makes it worth having closed.
 */

import React from "react";
import { Accordion, Icon, Label, Message } from "semantic-ui-react";

const Snip = ({ children }) => (
  <span className="cl-snip">{children}</span>
);

/** One line of the words-differ group — the group a reviewer reads first. */
const WordsDifferRow = ({ e }) => {
  if (e.what === "editorial") {
    return (
      <li className={`cl-row cl-${e.kind}`}>
        <Label size="mini" color={e.kind === "rewrite" ? "orange" : undefined}>{e.kind}</Label>
        <Snip>“{e.find}”</Snip> <Icon name="long arrow alternate right" size="small" />{" "}
        <Snip>“{e.replace}”</Snip>
        <span className="cl-meta">
          {e.targets} occurrence{e.targets === 1 ? "" : "s"}
          {/* ⚠️ A rewrite without a reason cannot be saved, so an empty one here means an older entry. */}
          {e.reason ? <> · {e.reason}</> : null}
        </span>
      </li>
    );
  }
  return (
    <li className={`cl-row cl-${e.kind}`}>
      <Label size="mini" color="orange">{e.kind}</Label>
      <span className="cl-meta">ch{e.chapterNumber}</span>
      <Snip>“{e.was}”</Snip>
      <div className="cl-parts">
        {e.now.map((p) => (
          <div key={p.ordinal} className="cl-part">
            <em>{p.speaker || "(no speaker)"}</em> · {p.type} — “{p.text}”
          </div>
        ))}
      </div>
      {e.reason ? <div className="cl-reason">{e.reason}</div> : null}
    </li>
  );
};

export const ChangeListPanel = ({ changeList, loading, error }) => {
  const [open, setOpen] = React.useState(false);
  const counts = changeList?.counts || { wordsDiffer: 0, structureOnly: 0, attributionOnly: 0 };
  const total = counts.wordsDiffer + counts.structureOnly + counts.attributionOnly;

  return (
    <Accordion styled fluid className="rs-change-list">
      <Accordion.Title active={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="dropdown" />
        Change list ({total})
        {/* ⭐ The words-differ count is promoted into the closed title, because it is the only one that can
            ever matter to somebody outside this room. */}
        {counts.wordsDiffer > 0 && (
          <Label size="mini" color="orange" style={{ marginLeft: ".6em" }}>
            {counts.wordsDiffer} where the audio differs from the book
          </Label>
        )}
      </Accordion.Title>
      <Accordion.Content active={open}>
        {loading && <div className="cl-empty">Reading…</div>}
        {error && <Message size="tiny" warning><Icon name="warning sign" /> {String(error)}</Message>}

        {!loading && !error && total === 0 && (
          <div className="cl-empty">
            Nothing has been changed. The audio says exactly what the book says.
          </div>
        )}

        {counts.wordsDiffer > 0 && (
          <div className="cl-group cl-group-serious">
            <h5>
              <Icon name="exclamation triangle" color="orange" />
              The audio says something the book does not ({counts.wordsDiffer})
            </h5>
            <ul className="cl-rows">
              {changeList.wordsDiffer.map((e, i) => <WordsDifferRow key={i} e={e} />)}
            </ul>
          </div>
        )}

        {counts.structureOnly > 0 && (
          <div className="cl-group">
            <h5>
              <Icon name="cut" />
              Lines split — the words are unchanged ({counts.structureOnly})
            </h5>
            {/* ⭐ Said plainly, because it is the difference between this group and the one above: a split is
                checked to add up to the original, so no reviewer needs to read these for content. */}
            <div className="cl-note">
              Each of these adds up to the original line exactly — checked when it was made and again every
              time it is read. Only the line boundaries and speakers moved.
            </div>
            <ul className="cl-rows">
              {changeList.structureOnly.map((e, i) => (
                <li key={i} className="cl-row">
                  <span className="cl-meta">ch{e.chapterNumber}</span>
                  <div className="cl-parts">
                    {e.now.map((p) => (
                      <div key={p.ordinal} className="cl-part">
                        <em>{p.speaker || "(no speaker)"}</em> · {p.type} — “{p.text}”
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {counts.attributionOnly > 0 && (
          <div className="cl-group">
            <h5><Icon name="user" />Who says it, and how ({counts.attributionOnly})</h5>
            <ul className="cl-rows">
              {changeList.attributionOnly.map((e, i) => (
                <li key={i} className="cl-row">
                  <span className="cl-meta">ch{e.chapterNumber}</span>
                  {e.speakerWas && (
                    <span className="cl-change">
                      speaker: <s>{e.speakerWas}</s> → <strong>{e.speaker}</strong>
                    </span>
                  )}
                  {/* ⚠️ A reassignment made before `speakerWas` was recorded shows the current speaker only.
                      "Not recorded" must not read as "unchanged" — 4 of Mask's 6 are in that state. */}
                  {!e.speakerWas && e.reassignedAt && (
                    <span className="cl-change">
                      speaker: <strong>{e.speaker}</strong>{" "}
                      <span className="cl-unknown">(previous value not recorded)</span>
                    </span>
                  )}
                  {e.emotionAt && (
                    <span className="cl-change">
                      emotion: <s>{e.emotionWas || "—"}</s> → <strong>{e.emotion}</strong>
                    </span>
                  )}
                  <Snip>“{e.snippet}”</Snip>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Accordion.Content>
    </Accordion>
  );
};

export default ChangeListPanel;
